import { useInfiniteQuery } from '@tanstack/react-query';
import { listTasksV2 } from 'services/TaskServices';
import { queryKeys } from '../../../config/queryClient';

/**
 * @param {string} columnId - The column/stage ID
 * @param {Object} filters - Optional filter parameters
 * @returns {Object} - Query result with data, fetchNextPage, hasNextPage, etc.
 */
export const useManageColumnQuery = (columnId, filters = {}) => {
  return useInfiniteQuery({
    queryKey: [...queryKeys.tasks.manage(columnId), filters],

    queryFn: async ({ pageParam = 1 }) => {
      const limit = 20; // Fixed limit for manage column

      // Build request payload
      const requestData = {
        task_stage_id: columnId,
        page: pageParam,
      };

      // Add filters to body if provided
      if (filters.practiceArea) {
        requestData.practice_area = filters.practiceArea;
      }
      if (filters.assignedTo && filters.assignedTo.length > 0) {
        requestData.firm_user_ids = filters.assignedTo;
      }
      if (filters.sortType) {
        requestData.sort_type = filters.sortType;
      }
      if (filters.priority) {
        requestData.priority = filters.priority;
      }
      if (filters.dueDateFilter) {
        requestData.due_date_filter = filters.dueDateFilter;
      }

      const response = await listTasksV2({
        limit,
        data: requestData,
        keyword: filters.searchKeyword || false,
        excludeFromLoading: true,
        case_id: filters.case_id || undefined,
      });

      return {
        items: response?.data?.items || [],
        page: response?.data?.page || pageParam,
        hasMore: response?.data?.has_more || false,
        totalCount: response?.data?.total_count || 0,
      };
    },

    // Get the next page parameter
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasMore) return undefined;
      return lastPage.page + 1;
    },

    // Get the previous page parameter
    getPreviousPageParam: (firstPage) => {
      if (firstPage.page <= 1) return undefined;
      return firstPage.page - 1;
    },

    // Enable query when columnId exists
    enabled: !!columnId,

    // Shorter staleTime for manage column (fresher data)
    staleTime: 30 * 1000, // 30 seconds

    // Refetch on mount to ensure fresh data
    refetchOnMount: 'always',

    // Don't refetch on window focus for modal
    refetchOnWindowFocus: false,

    notifyOnChangeProps: 'all',
  });
};
